/** How confident MAIA is that a source column maps to a Maptycs field. */
export enum FieldMatchStatus {
    /** Matched and confirmed. */
    CONFIRMED = "CONFIRMED",
    /** Matched, but the name only partially agrees — worth a human look. */
    REVIEW = "REVIEW",
    /** Nothing matched; the user picks a field or skips the column. */
    UNMATCHED = "UNMATCHED",
}

/** What the user decided to do with a mapping. */
export enum FieldMappingDecision {
    ACCEPTED = "ACCEPTED",
    SKIPPED = "SKIPPED",
    /** Reopened for editing — the field dropdown is live. */
    EDITING = "EDITING",
}

/** A Maptycs field a source column can be mapped onto. */
export interface MaiaFieldOption {
    /** Value sent back to the server. */
    id: string;
    /** Shown in the dropdown, e.g. "Year Built". */
    label: string;
}

/**
 * One row of the "Prepare your SOV" mapping table: a column from the
 * uploaded file, and the Maptycs field it will be imported as.
 */
export interface SovFieldMapping {
    /** Column name as it appears in the file, e.g. "yr_built". */
    sourceField: string;
    /** Chosen Maptycs field id; null when nothing matched or the row is skipped. */
    maiaFieldId: string | null;
    status: FieldMatchStatus;
    /** Why MAIA is unsure — shown beside the row, e.g. "Low confidence — …". */
    note: string;
    decision: FieldMappingDecision;
    /** Field type from the match suggestion, e.g. "SHORT_TEXT_FIELD" — carried through to the submit payload. */
    type: string | null;
    /**
     * System-managed record identifier / lat-lng / id column. Hidden from the
     * mapping table for now (not user-editable), but kept on every row —
     * not filtered out of the data — since these will need to be shown
     * again later.
     */
    rootField: boolean;
    coordinateField: boolean;
    idField: boolean;
    /** JIRA-SOV-015: already a field of this dataset — shown read-only, never edited. */
    existingField: boolean;
    /** JIRA-SOV-015: the user may not change this row's MAPTYCS field (root, coordinate, id or existing field). */
    locked: boolean;
    /**
     * JIRA-SOV-015: the readable name shown and edited on the Prepare screen — `maiaFieldId` is the
     * field id that gets saved, derived from this label by the backend on each save.
     */
    maiaFieldLabel: string | null;
}
